import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Flex,
  FormControl,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import AleaCheckIcon from '../../icons/AleaCheckIcon.tsx';
import AleaInput from '../../components/AleaInput/AleaInput.tsx';
import AleaPin from '../../components/AleaPin/AleaPin.tsx';
import { useAuth } from '../../context/AuthContext.tsx';
import CaregiverService, { ICaregiver } from '../../services/CaregiverService.ts';
import { DEFAULT_CAREGIVER_PIN_LENGTH } from '../../constants/caregiverPin';

interface AddCaregiverModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  carehomeId?: string;
  caregiver?: ICaregiver | null;
  /** PIN length rule (provider.pin_length). Defaults to 6. */
  pinLength?: number;
}

const AddCaregiverModal: React.FC<AddCaregiverModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  carehomeId,
  caregiver,
  pinLength = DEFAULT_CAREGIVER_PIN_LENGTH,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const toast = useToast();
  const [name, setName] = useState('');
  const [pin, setPin] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  const trimmedName = useMemo(() => name.trim(), [name]);
  const normalizedPin = useMemo(() => pin.replace(/\D/g, '').slice(0, pinLength), [pin, pinLength]);

  const isNameValid = trimmedName.length > 0;
  const isPinValid = normalizedPin.length === pinLength;
  const isEditMode = !!caregiver;

  // Reset / initialize form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setName(caregiver?.name || '');
      setPin(caregiver?.pin || '');
      setIsSubmitting(false);
      setHasAttemptedSubmit(false);
    } else {
      setName('');
      setPin('');
      setIsSubmitting(false);
      setHasAttemptedSubmit(false);
    }
  }, [isOpen, caregiver]);

  // Ensure header/backdrop z-index behaves correctly while modal is open
  useEffect(() => {
    try {
      const ev = new CustomEvent('alea-global-modal-open', { detail: isOpen });
      window.dispatchEvent(ev);
    } catch {}
    return () => {
      try {
        const ev = new CustomEvent('alea-global-modal-open', { detail: false });
        window.dispatchEvent(ev);
      } catch {}
    };
  }, [isOpen]);

  const handleSubmit = async () => {
    setHasAttemptedSubmit(true);
    if (!isNameValid || !isPinValid) return;

    const resolvedCarehomeId = carehomeId || user?.carehome?.id;
    if (!resolvedCarehomeId) {
      toast({
        title: t('error'),
        description: t('general.somethingWentWrong', { defaultValue: 'Something went wrong' }),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      setIsSubmitting(true);
      if (isEditMode && caregiver) {
        await CaregiverService.updateCaregiver(caregiver.id, {
          name: trimmedName,
          pin: normalizedPin,
          is_active: caregiver.is_active,
          carehome_id: caregiver.carehome?.id || resolvedCarehomeId,
        });
      } else {
        await CaregiverService.createCaregiver({
          name: trimmedName,
          pin: normalizedPin,
          is_active: true,
          carehome_id: resolvedCarehomeId,
        });
      }

      onConfirm?.();
      onClose();
    } catch (error: any) {
      console.error('Failed to create caregiver', error);

      toast({
        title: t('error'),
        description: t('careHome.caregiverAddFailed', { defaultValue: 'Failed to add caregiver' }),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay zIndex={1500} />
        <ModalContent
          data-testid="add-caregiver-modal"
          minWidth="500px"
          zIndex={1501}
          borderRadius="20px"
          bg="#F3F2F2"
        >
          <ModalHeader
            color="black"
            fontSize="25px"
            fontWeight={500}
            textAlign="left"
            px="50px"
            pt="50px"
            pb="0"
          >
            <Text
              as="div"
              data-testid="add-caregiver-modal-title"
              data-mode={isEditMode ? 'edit' : 'new'}
            >
              {t(isEditMode ? 'editCaregiver' : 'newCaregiver', {
                defaultValue: isEditMode ? 'Edit caregiver' : 'New caregiver',
              })}
            </Text>
          </ModalHeader>
          <ModalBody p="50px">
            <AleaInput
              label={t('name')}
              labelFontWeight={600}
              placeholder={t('caregiverNameExample', { defaultValue: 'e.g. Lisa Lustig' })}
              value={name}
              onChange={(e) => setName(e.target.value)}
              register={{
                name: 'name',
                onChange: async (_e: any) => {},
                onBlur: async (_e: any) => {},
                ref: () => {},
              }}
              error={
                hasAttemptedSubmit && !isNameValid
                  ? t('requiredField', { field: t('name') })
                  : undefined
              }
              isRequired
              sx={{
                height: '56px',
                borderRadius: '28px',
                border: '1px solid',
                borderColor: 'transparent',
                _placeholder: { color: 'gray.400' },
              }}
            />

            <FormControl mt="28px" isInvalid={hasAttemptedSubmit && !isPinValid} isRequired>
              <AleaPin
                label={t('pinForCareDocumentationSystem', {
                  defaultValue: 'PIN for care documentation system',
                })}
                value={normalizedPin}
                onChange={(value) => setPin(value)}
                length={pinLength}
                isDisabled={isSubmitting}
                isRequired
                error={
                  hasAttemptedSubmit && !isPinValid
                    ? t('pinLengthInvalid', {
                        length: pinLength,
                        defaultValue: `PIN must be ${pinLength} digits`,
                      })
                    : undefined
                }
              />
            </FormControl>
          </ModalBody>
          <ModalFooter pt={0} pb="50px" px="50px">
            <Flex justifyContent="flex-end" alignItems="center" width="100%">
              <Button
                data-testid="add-caregiver-modal-cancel"
                variant="link"
                onClick={onClose}
                color="alea.500"
                textDecoration="underline"
                fontSize="18px"
                fontWeight={400}
                isDisabled={isSubmitting}
              >
                {t('general.cancel')}
              </Button>

              <Button
                data-testid="add-caregiver-modal-save"
                colorScheme="alea"
                variant="solid"
                onClick={handleSubmit}
                isLoading={isSubmitting}
                isDisabled={isSubmitting}
                h="50px"
                w="100px"
                borderRadius="20px"
                px="0"
                display="flex"
                alignItems="center"
                justifyContent="center"
                ml="25px"
              >
                <AleaCheckIcon color="white" size={29} />
              </Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AddCaregiverModal;

